import React, { useState, useEffect } from 'react';
import { useNavigation } from '../../context/NavigationContext';
import { useAuth } from '../../../context/AuthContext';
import { PublicRoute } from '../../types/publicTypes';
import {
  Sparkles,
  Menu,
  X,
  LogIn,
  ArrowRight,
  ChevronRight,
  LayoutDashboard,
  LogOut,
  ChevronDown,
  User,
  Shield,
} from 'lucide-react';

export const PublicNavbar: React.FC = () => {
  const { currentPath, navigate } = useNavigation();
  const { currentUser, isAuthenticated, logout, isAdmin, isSuperAdmin } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks: { label: string; path: PublicRoute; icon?: any }[] = [
    { label: 'Platform', path: '/ozellikler' },
    { label: 'Çözümler', path: '/sektorler' },
    { label: 'Temalar', path: '/temalar' },
    { label: 'Paket Kapsamı', path: '/fiyatlandirma' },
    { label: 'Nasıl Çalışır', path: '/nasil-calisir' },
    { label: 'İletişim', path: '/iletisim' },
  ];

  const handleNavClick = (path: PublicRoute) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsUserDropdownOpen(false);
  };

  const handleLogout = async () => {
    setIsUserDropdownOpen(false);
    await logout();
    navigate('/');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        isScrolled
          ? 'bg-slate-950/85 backdrop-blur-md border-b border-slate-800/80 shadow-lg shadow-black/20'
          : 'bg-slate-950/50 backdrop-blur-sm border-b border-slate-800/40'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Brand Logo */}
          <div
            id="brand-logo-btn"
            onClick={() => handleNavClick('/')}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-sky-400 flex items-center justify-center shadow-md shadow-blue-600/30 group-hover:scale-105 transition-transform duration-200">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5">
                <span className="text-xl font-bold tracking-tight text-white font-sans">
                  Adio<span className="text-blue-400">soft</span>
                </span>
                <span className="text-[10px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                  Builder
                </span>
              </div>
              <span className="hidden sm:inline text-[11px] text-slate-400 font-medium tracking-wide truncate max-w-[180px]">
                Sektörel SaaS Website Platformu
              </span>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-1 bg-slate-900/60 p-1.5 rounded-full border border-slate-800/80">
            {navLinks.map((link) => {
              const isActive = currentPath === link.path;
              return (
                <button
                  key={link.path}
                  id={`nav-link-${link.path.replace('/', '') || 'home'}`}
                  onClick={() => handleNavClick(link.path)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-150 relative ${
                    isActive
                      ? 'text-white bg-slate-800 shadow-sm'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-blue-400" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right Action Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated && currentUser ? (
              <div className="flex items-center gap-3">
                {/* Direct Dashboard button */}
                <button
                  id="nav-authenticated-dashboard-btn"
                  onClick={() => handleNavClick(isSuperAdmin ? '/admin' : '/dashboard')}
                  className={`px-4 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg transition-all flex items-center gap-2 ${
                    isSuperAdmin
                      ? 'bg-rose-600 hover:bg-rose-500 shadow-rose-600/25'
                      : 'bg-blue-600 hover:bg-blue-500 shadow-blue-600/25'
                  }`}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  <span>{isSuperAdmin ? 'Yönetim Paneli' : 'Müşteri Paneli'}</span>
                </button>

                {/* User Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-slate-900 border border-slate-800 hover:bg-slate-800 transition"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-xs font-bold text-white">
                      {currentUser.displayName?.charAt(0) || currentUser.email?.charAt(0) || 'U'}
                    </div>
                    <span className="text-xs font-medium text-slate-200 max-w-[100px] truncate">
                      {currentUser.displayName || currentUser.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </button>

                  {isUserDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-3 z-50 animate-in fade-in zoom-in-95 duration-100">
                      <div className="pb-2 border-b border-slate-800 mb-2">
                        <div className="text-xs font-bold text-white truncate">
                          {currentUser.displayName || 'Kullanıcı'}
                        </div>
                        <div className="text-[11px] text-slate-400 truncate">{currentUser.email}</div>
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                            isSuperAdmin
                              ? 'bg-rose-500/10 text-rose-300 border border-rose-500/20'
                              : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                          }`}>
                            {isSuperAdmin ? 'Sistem Yöneticisi' : 'Müşteri'}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <button
                          onClick={() => handleNavClick(isSuperAdmin ? '/admin' : '/dashboard')}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-slate-200 hover:bg-slate-800 transition"
                        >
                          <LayoutDashboard className={`w-4 h-4 ${isSuperAdmin ? 'text-rose-400' : 'text-blue-400'}`} />
                          <span>{isSuperAdmin ? 'Şirket Yönetim Paneli' : 'Müşteri Paneline Git'}</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-rose-400 hover:bg-rose-500/10 transition font-medium"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Çıkış Yap</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <button
                  id="nav-login-btn"
                  onClick={() => handleNavClick('/giris')}
                  className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-150 flex items-center gap-2 ${
                    currentPath === '/giris'
                      ? 'text-white bg-slate-800'
                      : 'text-slate-300 hover:text-white hover:bg-slate-900/80'
                  }`}
                >
                  <LogIn className="w-4 h-4 text-slate-400" />
                  <span>Giriş Yap</span>
                </button>

                <button
                  id="nav-create-site-btn"
                  onClick={() => handleNavClick('/kayit')}
                  className="relative group overflow-hidden px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-sm font-semibold shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40 transition-all duration-200 active:scale-[0.98] flex items-center gap-2"
                >
                  <span>Site Oluştur</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle Button */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              id="mobile-menu-toggle-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2.5 rounded-xl text-slate-300 hover:text-white bg-slate-900 border border-slate-800 focus:outline-none"
              aria-label="Menüyü Aç/Kapat"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-b border-slate-800 px-4 pt-3 pb-6 shadow-2xl animate-in slide-in-from-top-2 duration-200">
          <div className="space-y-1">
            {navLinks.map((link) => {
              const isActive = currentPath === link.path;
              return (
                <button
                  key={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                    isActive
                      ? 'bg-blue-600/15 text-blue-400 border border-blue-500/20'
                      : 'text-slate-300 hover:bg-slate-900 hover:text-white'
                  }`}
                >
                  <span>{link.label}</span>
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                </button>
              );
            })}
            <button
              onClick={() => handleNavClick('/sss')}
              className="w-full flex items-center justify-between px-4 py-3 text-base font-medium rounded-xl text-slate-300 hover:bg-slate-900 hover:text-white"
            >
              <span>Sıkça Sorulan Sorular (SSS)</span>
              <ChevronRight className="w-4 h-4 text-slate-500" />
            </button>
            <button
              onClick={() => handleNavClick('/iletisim')}
              className="w-full flex items-center justify-between px-4 py-3 text-base font-medium rounded-xl text-slate-300 hover:bg-slate-900 hover:text-white"
            >
              <span>İletişim & Destek</span>
              <ChevronRight className="w-4 h-4 text-slate-500" />
            </button>
          </div>

          <div className="pt-4 mt-4 border-t border-slate-800/80">
            {isAuthenticated && currentUser ? (
              <div className="space-y-2">
                <button
                  onClick={() => handleNavClick('/dashboard')}
                  className="w-full py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm text-center shadow-lg shadow-blue-600/30 transition flex items-center justify-center gap-2"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  <span>Yönetici Paneline Git</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full py-2.5 px-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400 font-semibold text-xs text-center hover:bg-rose-500/20 transition flex items-center justify-center gap-2"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span>Çıkış Yap</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleNavClick('/giris')}
                  className="w-full py-3 px-4 rounded-xl border border-slate-700 bg-slate-900 text-slate-200 font-medium text-sm text-center hover:bg-slate-800 transition"
                >
                  Giriş Yap
                </button>
                <button
                  onClick={() => handleNavClick('/kayit')}
                  className="w-full py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm text-center shadow-lg shadow-blue-600/30 transition"
                >
                  Site Oluştur
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
